import { ArrowLeft, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getEmployeeBillsDetail } from '../../../api/employee.api';
import styles from '../../../assets/scss/pages/EmployeeList.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';

function fmtNum(v) {
    return Math.floor(Number(v) || 0).toLocaleString('vi-VN');
}

function fmtDateTime(str) {
    if (!str) return '—';
    const d = new Date(str);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

export default function SalaryDetail() {
    const { employeeId } = useParams();
    const [searchParams] = useSearchParams();
    const month = searchParams.get('month') || '';
    const employeeName = decodeURIComponent(searchParams.get('name') || '');
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canView = hasPermission(PERMISSIONS.SALARY.VIEW);

    const [bills, setBills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedBills, setExpandedBills] = useState(new Set());

    const fetchData = useCallback(async () => {
        if (!canView) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const { data } = await getEmployeeBillsDetail(employeeId, { month });
            setBills(data.bills || []);
        } catch {
            setBills([]);
        } finally {
            setLoading(false);
        }
    }, [canView, employeeId, month]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const toggleBill = (billId) => {
        setExpandedBills((prev) => {
            const next = new Set(prev);
            if (next.has(billId)) next.delete(billId);
            else next.add(billId);
            return next;
        });
    };

    const totalRevenue = bills.reduce(
        (sum, b) => sum + (b.items || []).reduce((s, it) => s + (Number(it.subtotal) || 0), 0),
        0,
    );
    const totalCommission = bills.reduce((sum, b) => sum + (Number(b.totalCommission) || 0), 0);

    const monthDisplay = month
        ? new Date(month + '-01').toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' })
        : '';

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div className={styles.headerLeft} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <button
                        style={{
                            width: 40,
                            height: 40,
                            border: '1.5px solid #ECEEF2',
                            borderRadius: 10,
                            background: '#fff',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        onClick={() => navigate(-1)}
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className={styles.title} style={{ fontSize: 20, marginBottom: 2 }}>
                            Chi tiết lương thưởng
                        </h1>
                        <p className={styles.subtitle}>
                            {employeeName} — {monthDisplay}
                        </p>
                    </div>
                </div>
            </div>

            {/* Summary */}
            {!loading && bills.length > 0 && (
                <div className={styles.kpis}>
                    <div className={styles.kpi}>
                        <div className={styles.kpiContent}>
                            <span className={styles.kpiLabel}>Tổng doanh thu</span>
                            <span className={styles.kpiValue} style={{ color: '#0098F6' }}>
                                {fmtNum(totalRevenue)} đ
                            </span>
                        </div>
                    </div>
                    <div className={styles.kpi}>
                        <div className={styles.kpiContent}>
                            <span className={styles.kpiLabel}>Tổng hoa hồng</span>
                            <span className={styles.kpiValue} style={{ color: '#F59E0B' }}>
                                {fmtNum(totalCommission)} đ
                            </span>
                        </div>
                    </div>
                    <div className={styles.kpi}>
                        <div className={styles.kpiContent}>
                            <span className={styles.kpiLabel}>Số hoá đơn</span>
                            <span className={styles.kpiValue}>{bills.length}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Bills */}
            {loading ? (
                <div className={styles.loading}>
                    <div className={styles.spinner} /> Đang tải...
                </div>
            ) : bills.length === 0 ? (
                <div className={styles.empty}>Không có hoá đơn nào trong tháng này</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {bills.map((bill) => {
                        const billRevenue = (bill.items || []).reduce((s, it) => s + (Number(it.subtotal) || 0), 0);
                        const isExpanded = expandedBills.has(bill.billId);
                        return (
                            <div
                                key={bill.billId}
                                style={{
                                    background: '#fff',
                                    border: '1.5px solid #ECEEF2',
                                    borderRadius: 14,
                                    overflow: 'hidden',
                                    transition: 'border-color 0.15s',
                                }}
                            >
                                {/* Bill header */}
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '14px 20px',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => toggleBill(bill.billId)}
                                >
                                    <div>
                                        <div
                                            style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A', marginBottom: 4 }}
                                        >
                                            {bill.billCode || bill.code || bill.billId} —{' '}
                                            {bill.customerName || 'Khách lẻ'}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#6B7280' }}>
                                            {fmtDateTime(bill.createdAt)}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: 14, fontWeight: 600, color: '#0098F6' }}>
                                                {fmtNum(billRevenue)} đ
                                            </div>
                                            {Number(bill.totalCommission) > 0 && (
                                                <div style={{ fontSize: 12, color: '#F59E0B' }}>
                                                    HH: {fmtNum(bill.totalCommission)} đ
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 4,
                                                padding: '6px 12px',
                                                border: '1.5px solid #ECEEF2',
                                                borderRadius: 8,
                                                background: '#fff',
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: '#0098F6',
                                                cursor: 'pointer',
                                                fontFamily: 'inherit',
                                                whiteSpace: 'nowrap',
                                                transition: 'all 0.15s',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = '#E6F4FF';
                                                e.currentTarget.style.borderColor = '#0098F6';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = '#fff';
                                                e.currentTarget.style.borderColor = '#ECEEF2';
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/admin/bills/${bill.billId}`);
                                            }}
                                        >
                                            <ExternalLink size={12} /> Xem HĐ
                                        </button>
                                        {isExpanded ? (
                                            <ChevronUp size={18} color="#6B7280" />
                                        ) : (
                                            <ChevronDown size={18} color="#6B7280" />
                                        )}
                                    </div>
                                </div>

                                {/* Bill items */}
                                {isExpanded && (
                                    <div style={{ borderTop: '1px solid #ECEEF2', padding: '12px 20px' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                            <thead>
                                                <tr
                                                    style={{
                                                        color: '#6B7280',
                                                        fontWeight: 600,
                                                        fontSize: 11,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.04em',
                                                    }}
                                                >
                                                    <th style={{ textAlign: 'left', padding: '6px 0' }}>Dịch vụ</th>
                                                    <th style={{ textAlign: 'right', padding: '6px 0' }}>
                                                        SL × Đơn giá
                                                    </th>
                                                    <th style={{ textAlign: 'right', padding: '6px 0' }}>Thành tiền</th>
                                                    <th style={{ textAlign: 'right', padding: '6px 0' }}>Hoa hồng</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(bill.items || []).map((item, i) => (
                                                    <tr key={i} style={{ borderTop: '1px solid #F3F4F6' }}>
                                                        <td style={{ padding: '8px 0', color: '#1A1A1A' }}>
                                                            {item.serviceName}
                                                            {item.variantName && (
                                                                <span style={{ color: '#6B7280' }}>
                                                                    {' '}
                                                                    - {item.variantName}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td
                                                            style={{
                                                                textAlign: 'right',
                                                                padding: '8px 0',
                                                                color: '#6B7280',
                                                            }}
                                                        >
                                                            {item.quantity} × {fmtNum(item.price)}
                                                        </td>
                                                        <td
                                                            style={{
                                                                textAlign: 'right',
                                                                padding: '8px 0',
                                                                fontWeight: 600,
                                                                color: '#1A1A1A',
                                                            }}
                                                        >
                                                            {fmtNum(item.subtotal)}
                                                        </td>
                                                        <td
                                                            style={{
                                                                textAlign: 'right',
                                                                padding: '8px 0',
                                                                color: '#F59E0B',
                                                                fontWeight: 500,
                                                            }}
                                                        >
                                                            {Number(item.commission) > 0
                                                                ? `${fmtNum(item.commission)} đ (${item.commissionType === 'percent' ? '%' : 'VNĐ'})`
                                                                : '—'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
